// Browser-local custom ordering of the user's dashboards (experimental feature,
// gated by the `showComponentReorderingMenu` flag). Overrides the default
// created_at sort so the user controls which dashboard loads first (order[0]).
// Persisted in localStorage only — per-browser, not cross-device.

const STORAGE_KEY = "dashalt_dashboard_order";

export function getDashboardOrder(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setDashboardOrder(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // localStorage unavailable — ordering simply doesn't persist.
  }
}

export function clearDashboardOrder(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Reorder `list` by the saved order. Items in the saved order come first (in that
 * order); any not present keep their incoming relative order at the end (the
 * caller passes a created_at-sorted list, so new dashboards fall in by date).
 * Array.sort is stable, so equal-rank (unsaved) items retain their input order.
 */
export function applyDashboardOrder<T extends { dashboard_id: string }>(list: T[]): T[] {
  const order = getDashboardOrder();
  if (order.length === 0) return list;
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...list].sort(
    (a, b) => (rank.get(a.dashboard_id) ?? Infinity) - (rank.get(b.dashboard_id) ?? Infinity),
  );
}
